import {
  Directive,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  Output,
  TemplateRef,
  forwardRef
} from "@angular/core";
import { Subscription } from "rxjs";

import { ModalRef, ModalService } from "@lantern/app/core/utils/modals";
import { PositionOption } from "@lantern/app/core/utils/overlays";

export interface ModalTemplateContext<Response> {
  $implicit: {
    close(response: Response): void;
    dismiss(err?: any): void;
  };
  close(response: Response): void;
  dismiss(err?: any): void;
}

@Directive({
  selector: "[taModalTemplate]",
  exportAs: "taModalTemplate"
})
export class ModalTemplateDirective<Response> implements OnDestroy {
  @Input()
  position: PositionOption = "center";

  @Input()
  windowPadding: number = 20;

  modalRef: ModalRef<Response> | undefined;

  get opened(): boolean {
    return this.modalRef != null;
  }

  // tslint:disable-next-line:no-output-native
  @Output()
  close: EventEmitter<Response> = new EventEmitter<Response>();

  @Output()
  dismiss: EventEmitter<any> = new EventEmitter<any>();

  constructor(
    @Inject(forwardRef(() => ModalService)) private modalService: ModalService,
    private templateRef: TemplateRef<ModalTemplateContext<Response>>
  ) {}

  ngOnDestroy(): void {
    if (this.modalRef != null) {
      this.modalRef.dismiss();
    }
    if (this.modalRefSubscription != null) {
      this.modalRefSubscription.unsubscribe();
    }
  }

  private modalRefSubscription: Subscription | undefined;
  open(): ModalRef<Response> {
    if (this.modalRef != null) {
      return this.modalRef;
    }

    this.modalRef = this.modalService.openTemplate(
      this.templateRef,
      undefined,
      this.position,
      this.windowPadding
    );

    this.modalRefSubscription = this.modalRef.response.subscribe(
      (res) => {
        this.close.emit(res);
        this.cleanupModal();
      },
      (err) => {
        this.dismiss.emit(err);
        this.cleanupModal();
      }
    );

    return this.modalRef;
  }

  private cleanupModal(): void {
    if (this.modalRefSubscription != null) {
      this.modalRefSubscription.unsubscribe();
    }
    this.modalRefSubscription = undefined;
    this.modalRef = undefined;
  }
}
